import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Briefcase, TrendingUp, Clock, Calendar, Activity } from 'lucide-react';
import type { JobApplication, JobStatus } from '../types';
import { fetchJobApplications } from '../lib/jobApi';

const STATUS_COLORS: Record<JobStatus, string> = {
  saved: 'bg-gray-500',
  applied: 'bg-blue-500',
  phone_screen: 'bg-yellow-500',
  interview: 'bg-orange-500',
  technical: 'bg-purple-500',
  offer: 'bg-green-500',
  rejected: 'bg-red-500',
  ghosted: 'bg-gray-700',
};

const STATUS_LABELS: Record<JobStatus, string> = {
  saved: 'Saved',
  applied: 'Applied',
  phone_screen: 'Phone Screen',
  interview: 'Interview',
  technical: 'Technical',
  offer: 'Offer',
  rejected: 'Rejected',
  ghosted: 'Ghosted',
};

const SOURCE_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  helloworld: 'HelloWorld',
  jooble: 'Jooble',
  indeed: 'Indeed',
  glassdoor: 'Glassdoor',
  direct_contact: 'Direct Contact',
  referral: 'Referral',
  company_website: 'Company Website',
  other: 'Other',
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function daysBetween(d1: string, d2: string): number {
  const ms = new Date(d2).getTime() - new Date(d1).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

// ─── Stat Card ───

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string }> = ({ icon, label, value, sub, color = 'text-blue-400' }) => (
  <div className="bg-theme-primary rounded-lg border border-theme-primary p-4">
    <div className="flex items-center gap-2 mb-1">
      <span className={color}>{icon}</span>
      <span className="text-xs text-theme-secondary">{label}</span>
    </div>
    <p className="text-2xl font-bold text-theme-primary">{value}</p>
    {sub && <p className="text-xs text-theme-secondary mt-0.5">{sub}</p>}
  </div>
);

// ─── Main ───

const JobDashboard: React.FC = () => {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchJobApplications();
      setApplications(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = applications.length;

    const byStatus: Record<string, number> = {};
    const allStatuses: JobStatus[] = ['saved', 'applied', 'phone_screen', 'interview', 'technical', 'offer', 'rejected', 'ghosted'];
    allStatuses.forEach((s) => (byStatus[s] = 0));
    applications.forEach((a) => (byStatus[a.status] = (byStatus[a.status] || 0) + 1));

    const bySource: Record<string, number> = {};
    applications.forEach((a) => {
      const src = a.source || 'unknown';
      bySource[src] = (bySource[src] || 0) + 1;
    });

    const progressedStatuses = new Set<JobStatus>(['phone_screen', 'interview', 'technical', 'offer']);
    const applied = applications.filter((a) => a.status !== 'saved');
    const progressed = applications.filter((a) => progressedStatuses.has(a.status) || a.status === 'offer');
    const responseRate = applied.length > 0 ? Math.round((progressed.length / applied.length) * 100) : 0;

    // Avg response time: days between applied_date and updated_at for apps that progressed past applied
    const responseTimes: number[] = [];
    applications.forEach((a) => {
      if (a.applied_date && progressedStatuses.has(a.status)) {
        const days = daysBetween(a.applied_date, a.updated_at);
        if (days >= 0) responseTimes.push(days);
      }
    });
    const avgResponseTime = responseTimes.length > 0
      ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
      : null;

    const now = new Date();
    const upcomingFollowUps = applications
      .filter((a) => a.follow_up_date && new Date(a.follow_up_date) >= now)
      .sort((a, b) => new Date(a.follow_up_date!).getTime() - new Date(b.follow_up_date!).getTime())
      .slice(0, 5);

    const upcomingInterviews = applications
      .filter((a) => a.interview_date && new Date(a.interview_date) >= now)
      .sort((a, b) => new Date(a.interview_date!).getTime() - new Date(b.interview_date!).getTime())
      .slice(0, 5);

    const recent = [...applications]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 5);

    return { total, byStatus, bySource, responseRate, avgResponseTime, upcomingFollowUps, upcomingInterviews, recent, allStatuses };
  }, [applications]);

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-secondary pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <p className="text-center text-theme-secondary py-12">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl font-bold text-theme-primary mb-2">Dashboard</h1>
        <p className="text-theme-secondary mb-6">Overview of your job hunt progress</p>

        {error && (
          <div className="bg-red-500/10 text-red-400 rounded-lg p-3 text-sm mb-4">{error}</div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard icon={<Briefcase className="w-4 h-4" />} label="Total" value={stats.total} />
          <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Response Rate" value={`${stats.responseRate}%`}
            sub={`${stats.byStatus['offer'] || 0} offers`} color="text-green-400" />
          <StatCard icon={<Clock className="w-4 h-4" />} label="Avg Response" value={stats.avgResponseTime !== null ? `${stats.avgResponseTime}d` : '—'}
            sub="days to hear back" color="text-yellow-400" />
          <StatCard icon={<Activity className="w-4 h-4" />} label="Active"
            value={stats.total - (stats.byStatus['rejected'] || 0) - (stats.byStatus['ghosted'] || 0)}
            sub="not rejected/ghosted" color="text-purple-400" />
        </div>

        {/* Status breakdown */}
        <div className="bg-theme-primary rounded-lg border border-theme-primary p-4 mb-4">
          <h3 className="text-sm font-semibold text-theme-primary mb-3">By Status</h3>
          <div className="space-y-2">
            {stats.allStatuses.map((status) => {
              const count = stats.byStatus[status] || 0;
              const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={status} className="flex items-center gap-2 text-xs">
                  <span className="w-24 text-right text-theme-secondary">{STATUS_LABELS[status]}</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-300 ${STATUS_COLORS[status]}`}
                      style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-theme-secondary font-medium">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Source breakdown */}
        {Object.keys(stats.bySource).length > 0 && (
          <div className="bg-theme-primary rounded-lg border border-theme-primary p-4 mb-4">
            <h3 className="text-sm font-semibold text-theme-primary mb-3">By Source</h3>
            <div className="space-y-2">
              {Object.entries(stats.bySource)
                .sort(([, a], [, b]) => b - a)
                .map(([src, count]) => {
                  const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
                  return (
                    <div key={src} className="flex items-center gap-2 text-xs">
                      <span className="w-28 text-right text-theme-secondary truncate">
                        {SOURCE_LABELS[src] || src}
                      </span>
                      <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-300 bg-blue-500"
                          style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-12 text-theme-secondary font-medium">{count} ({pct.toFixed(0)}%)</span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Upcoming interviews */}
        {stats.upcomingInterviews.length > 0 && (
          <div className="bg-theme-primary rounded-lg border border-theme-primary p-4 mb-4">
            <h3 className="text-sm font-semibold text-theme-primary mb-3 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-orange-400" /> Upcoming Interviews
            </h3>
            <div className="space-y-2">
              {stats.upcomingInterviews.map((app) => (
                <div key={app.id} className="flex items-center justify-between text-xs">
                  <div>
                    <span className="font-medium text-theme-primary">{app.company}</span>
                    <span className="text-theme-secondary ml-1.5">{app.position}</span>
                  </div>
                  <span className="text-orange-400 font-medium">{formatDateTime(app.interview_date!)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming follow-ups */}
        {stats.upcomingFollowUps.length > 0 && (
          <div className="bg-theme-primary rounded-lg border border-theme-primary p-4 mb-4">
            <h3 className="text-sm font-semibold text-theme-primary mb-3 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-yellow-400" /> Upcoming Follow-ups
            </h3>
            <div className="space-y-2">
              {stats.upcomingFollowUps.map((app) => (
                <div key={app.id} className="flex items-center justify-between text-xs">
                  <div>
                    <span className="font-medium text-theme-primary">{app.company}</span>
                    <span className="text-theme-secondary ml-1.5">{app.position}</span>
                  </div>
                  <span className="text-yellow-400 font-medium">{formatDate(app.follow_up_date!)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent activity */}
        {stats.recent.length > 0 && (
          <div className="bg-theme-primary rounded-lg border border-theme-primary p-4">
            <h3 className="text-sm font-semibold text-theme-primary mb-3 flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-blue-400" /> Recent Activity
            </h3>
            <div className="space-y-2">
              {stats.recent.map((app) => {
                const statusInfo = STATUS_LABELS[app.status];
                const statusColor = STATUS_COLORS[app.status];
                return (
                  <div key={app.id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`shrink-0 w-2 h-2 rounded-full ${statusColor}`} />
                      <span className="font-medium text-theme-primary truncate">{app.company}</span>
                      <span className="text-theme-secondary truncate">{app.position}</span>
                    </div>
                    <span className="text-theme-secondary shrink-0 ml-2">{statusInfo}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {applications.length === 0 && (
          <p className="text-center text-theme-secondary py-12">No applications yet. Start tracking!</p>
        )}
      </div>
    </div>
  );
};

export default JobDashboard;
